import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { UtilService } from '../services/util.service';
import { selectUserMenuList } from 'src/app/store/userMenuList/userMenuList.selector';
import { ADMIN, DAAS, UNAUTH } from 'src/app/shared/routeLink.constant';

/**
 * The embedded DAAS app (admin/daas/app) is not a menu entry, so AuthGuard's menu lookup
 * would reject it. Allow it for users holding the "Open" action on the DAAS screen,
 * the same permission that shows the "Open DAAS" button.
 */
export const DaasAppGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const store = inject(Store);
  const utils = inject(UtilService);
  if (authService.checkConsentPending()) {
    return false;
  }
  if (!authService.checkIsUserLogin()) {
    utils.redirectToLogin();
    return false;
  }

  return store.select(selectUserMenuList).pipe(
    filter((menu) => !!menu && menu.length > 0),
    take(1),
    switchMap(() => utils.hasPermission(`${ADMIN}/${DAAS}`, 'open')),
    map((hasAccess) => {
      if (!hasAccess) {
        router.navigate([UNAUTH]);
        return false;
      }

      return true;
    })
  );
};

/**
 * DAAS pages opened from the DAAS menu (/daas/<page>/...). The DAAS app has nested pages
 * (e.g. daas/vendors/ABC/vehicles) that are not menu entries, so allow any URL under a
 * DAAS submenu the user has instead of AuthGuard's exact match.
 */
export const DaasMenuGuard: CanActivateFn = (_route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const store = inject(Store);
  const utils = inject(UtilService);
  if (authService.checkConsentPending()) {
    return false;
  }
  if (!authService.checkIsUserLogin()) {
    utils.redirectToLogin();
    return false;
  }

  // Menu route_links have no leading slash
  const url = state.url.split('?')[0].slice(1);

  return store.select(selectUserMenuList).pipe(
    filter((menu) => !!menu && menu.length > 0),
    take(1),
    map((menuList) => {
      if (utils.getMenuActions(menuList, url) === -1) {
        router.navigate([UNAUTH]);
        return false;
      }

      return true;
    })
  );
};
